import type { MultiPolygon } from 'geojson'
import type { LayerProps, MapMouseEvent, MapTouchEvent } from 'react-map-gl'
import type { Client, Parcel, ParcelProperties } from '~/models'

import { useQuery } from '@tanstack/react-query'
import bbox from '@turf/bbox'
import { useEffect, useMemo, useState } from 'react'
import { Layer, Source, useMap } from 'react-map-gl'
import {
  aDrawMode,
  aDrawnFeature,
  aMapStyle,
  aParcelDataResidentialVisible,
  useAtomValue,
} from '~/atoms'
import { getParcelsByPoint } from '~/query'
import { MobilePopup } from '../components'
import MapParcelPopup from './parcel-popup'

type ParcelLayerProps = {
  client: Client
}

type Coordinates = {
  lat: number
  lon: number
}

type ParcelClickEvent = (MapMouseEvent | MapTouchEvent) & {
  features?: mapboxgl.MapboxGeoJSONFeature[] | undefined
} & mapboxgl.EventData

const beforeId = 'z-index-3'
const tileSourceId = 'parcel-data'
const sourceLayerId = 'parcels' // must match mbtiles layer name
const tiles = 'https://serve.resimplifi.com/api/v1/tiles/parcels/{z}/{x}/{y}.mvt'
const tileFillId = 'parcels-fills'
const tileMinZoom = 13
const tileMaxZoom = 15

export default function ParcelLayer({ client }: ParcelLayerProps) {
  const mapRef = useMap()
  const mapType = useAtomValue(aMapStyle)
  const parcelDataResidentialVisible = useAtomValue(
    aParcelDataResidentialVisible
  )
  const [selectedParcel, setSelectedParcel] = useState<Parcel | null>(null)
  const [selectedParcelId, setSelectedParcelId] = useState<string | null>(null)
  const [selectedParcelIndex, setSelectedParcelIndex] = useState<number | null>(
    null
  )
  const drawMode = useAtomValue(aDrawMode)
  const drawFeature = useAtomValue(aDrawnFeature)

  const [popupCoordinates, setPopupCoordinates] = useState<Coordinates>({
    lat: 0,
    lon: 0,
  })

  const parcelQuery = useQuery({
    queryKey: ['parcel', selectedParcel?.ll_uuid],
    queryFn: () =>
      getParcelsByPoint({
        lat: popupCoordinates.lat,
        lon: popupCoordinates.lon,
      }),
    enabled: !!selectedParcel?.ll_uuid,
  })

  const maxBounds = useMemo(() => {
    if (!client.geom) {
      return
    }
    return bbox(client.geom as MultiPolygon)
  }, [])

  const parcels: ParcelProperties[] = useMemo(() => {
    if (
      parcelQuery.data?.parcels &&
      parcelQuery.data.parcels.features.length > 0
    ) {
      setSelectedParcelIndex(0)

      return parcelQuery.data.parcels.features.map((feature: any) => {
        return feature.properties as ParcelProperties
      })
    }

    return []
  }, [parcelQuery.data])

  const selectedParcelData = useMemo(() => {
    if (selectedParcelIndex === null) return null
    return parcels[selectedParcelIndex] ?? null
  }, [parcels, selectedParcelIndex])

  function onPopupClose() {
    if (mapRef.current! && selectedParcelId) {
      mapRef.current!.setFeatureState(
        {
          source: tileSourceId,
          sourceLayer: sourceLayerId,
          id: selectedParcelId,
        },
        { selected: false }
      )
    }
    setSelectedParcel(null)
  }

  useEffect(() => {
    if (mapRef.current && !!client.geom && !drawMode) {
      const map = mapRef.current?.getMap()
      let hoveredPolygonId: string | null = null
      let isListingClicked = false
      if (!map) {
        return
      }

      let touchStartTime: number | null = null
      let touchStartPosition: { x: number; y: number } | null = null

      function onMouseMove(e: ParcelClickEvent) {
        isListingClicked = false

        if ((e.features || []).length > 0) {
          if (hoveredPolygonId !== null) {
            mapRef.current!.getMap().getCanvasContainer().style.cursor = 'grab'
            map.setFeatureState(
              {
                source: tileSourceId,
                sourceLayer: sourceLayerId,
                id: hoveredPolygonId,
              },
              { hover: false }
            )
          }
          hoveredPolygonId = e.features![0].id as string
          mapRef.current!.getMap().getCanvasContainer().style.cursor = 'pointer'
          map.setFeatureState(
            {
              source: tileSourceId,
              sourceLayer: sourceLayerId,
              id: hoveredPolygonId,
            },
            { hover: true }
          )
        }
      }

      function onParcelClick(e: ParcelClickEvent) {
        if (!isListingClicked && (e.features || []).length > 0) {
          const clickedFeature = e.features![0]
          const clickedId = clickedFeature.id as string

          setPopupCoordinates({
            lat: e.lngLat.lat,
            lon: e.lngLat.lng,
          })

          setSelectedParcel(clickedFeature.properties as ParcelProperties)

          if (selectedParcelId) {
            map.setFeatureState(
              {
                source: tileSourceId,
                sourceLayer: sourceLayerId,
                id: selectedParcelId,
              },
              { selected: false }
            )
          }

          setSelectedParcelId(clickedId)

          map.setFeatureState(
            {
              source: tileSourceId,
              sourceLayer: sourceLayerId,
              id: clickedId,
            },
            { selected: true }
          )
        }
        isListingClicked = false
      }

      function onTouchStart(e: mapboxgl.MapTouchEvent) {
        touchStartTime = Date.now()
        touchStartPosition = { x: e.point.x, y: e.point.y }
      }

      function onTouchEnd(e: ParcelClickEvent) {
        if (touchStartTime === null || touchStartPosition === null) return

        const touchEndTime = Date.now()
        const touchEndPosition = { x: e.point.x, y: e.point.y }

        const touchDistanceX = Math.abs(
          touchStartPosition.x - touchEndPosition.x
        )
        const touchDistanceY = Math.abs(
          touchStartPosition.y - touchEndPosition.y
        )
        const touchDuration = touchEndTime - touchStartTime

        const maxDistance = 10
        const maxDuration = 300

        const isClick =
          touchDuration < maxDuration &&
          touchDistanceX < maxDistance &&
          touchDistanceY < maxDistance

        touchStartTime = null
        touchStartPosition = null

        if (isClick) {
          onParcelClick(e)
        } else {
          onMouseMove(e)
        }

        if (hoveredPolygonId !== null) {
          map.setFeatureState(
            {
              source: tileSourceId,
              sourceLayer: sourceLayerId,
              id: hoveredPolygonId,
            },
            { hover: false }
          )
        }
      }

      const onPropertyClick = (e: ParcelClickEvent) => {
        if (e.features && e.features.length > 0) {
          isListingClicked = true
          setSelectedParcel(null)
        } else {
          isListingClicked = false
        }
      }

      map.on('click', 'property-single-clusters', onPropertyClick)
      map.on('touchstart', 'property-single-clusters', onPropertyClick)
      map.on('click', tileFillId, onParcelClick)
      map.on('touchstart', tileFillId, onTouchStart)
      map.on('touchend', tileFillId, onTouchEnd)
      map.on('mousemove', tileFillId, onMouseMove)

      map.on('mouseleave', tileFillId, () => {
        isListingClicked = false
        if (hoveredPolygonId !== null) {
          mapRef.current!.getMap().getCanvasContainer().style.cursor = 'grab'
          map.setFeatureState(
            {
              source: tileSourceId,
              sourceLayer: sourceLayerId,
              id: hoveredPolygonId,
            },
            { hover: false }
          )
        }
        hoveredPolygonId = null
      })
    }
  }, [mapRef.current, selectedParcelId])

  const layerStyle = useMemo(() => {
    const style: LayerProps = {
      id: 'parcels',
      'source-layer': sourceLayerId,
      type: 'line',
      // interactive: true,
      paint: {
        'line-color': mapType === 'street' ? '#000' : '#fff',
        'line-opacity': mapType === 'street' ? 0.15 : 0.5,
      },
      layout: {},
    }

    return style
  }, [mapType, sourceLayerId])

  const layerStyleFill: LayerProps = {
    id: tileFillId,
    type: 'fill',
    'source-layer': sourceLayerId,
    layout: {},
    paint: {
      'fill-color': [
        'case',
        ['boolean', ['feature-state', 'selected'], false],
        '#0284c7',
        ['boolean', ['feature-state', 'hover'], false],
        '#0284c7',
        '#000000',
      ],
      'fill-opacity': [
        'case',
        ['boolean', ['feature-state', 'selected'], false],
        0.5,
        ['boolean', ['feature-state', 'hover'], false],
        0.25,
        0.1,
      ],
    },
  }

  if (parcelDataResidentialVisible === false) {
    layerStyleFill.filter = ['!=', ['get', 'zoning_type'], 'Residential']
  }

  if (!client.geom || !!drawMode || !!drawFeature) {
    return null
  }

  return (
    <>
      <Source
        id={tileSourceId}
        type="vector"
        tiles={[tiles]}
        minzoom={tileMinZoom}
        maxzoom={tileMaxZoom}
        bounds={maxBounds}
        promoteId="ll_uuid">
        <Layer beforeId={beforeId} id="parcel-style-fill" {...layerStyleFill} />
        <Layer beforeId={beforeId} {...layerStyle} />

        {selectedParcel && (
          <MobilePopup
            className="show-close !m-0 w-full max-w-[400px] select-text !p-0"
            maxWidth="400px"
            key={selectedParcel.ll_uuid}
            latitude={popupCoordinates.lat}
            longitude={popupCoordinates.lon}
            closeButton={true}
            closeOnMove={true}
            closeOnClick={false}
            ariaLabel="Parcel details"
            onClose={onPopupClose}
            style={{ zIndex: 48 }}>
            <MapParcelPopup
              parcels={parcels}
              selectedParcelData={selectedParcelData}
              selectedParcelIndex={selectedParcelIndex}
              setSelectedParcelIndex={setSelectedParcelIndex}
            />
          </MobilePopup>
        )}
      </Source>
    </>
  )
}
